import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { ApiError } from "@tdelivr/api-client";
import type { Transport } from "@tdelivr/api-client";
import { createDispatchConsoleBffClient } from "@tdelivr/api-client/generated/experience/dispatch-console-bff.js";
import type {
  CountFloor,
  DeliveryDetail,
  DispatchDetail,
  LogisticsDetail,
  SourceReport,
  TriageRow as AggregateRow,
} from "@tdelivr/api-client/generated/experience/dispatch-console-bff.js";

import {
  badgeLabel,
  formatAge,
  formatClock,
  stateClassOf,
  triagePage,
  TRIAGE_SOURCES,
  TRIAGE_TILES,
  type DetailField,
  type TriageOrder,
  type TriageRow,
  type TriageSource,
} from "../triage.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { ExceptionDetail } from "./ExceptionDetail.tsx";
import { PageHeading } from "./PageHeading.tsx";
import { StateBadge } from "./StateBadge.tsx";
import { StateView } from "./StateView.tsx";

/**
 * The screen operators live in: one queue merging three sources, worst first, beside the selected row.
 *
 * DEL-08 delivery exceptions, LOG-11 logistics exceptions, and the failed or deadline-bound searches
 * in DSP-05's dispatch read model — merged, bucketed, ranked and bounded by EXP-008 rather than here.
 * R-05 in the reconciliation is why: three separately keyset-paged sources cannot be paged as one,
 * and the ranking *between* three notions of urgent is one rule that no single service publishes and
 * two consoles must not each guess at. `triage.ts`'s own header said this belonged in the dispatch
 * BFF when one existed; it exists, and R-14 records the move.
 *
 * **One request, one scope.** `dispatch-console:read` is a gate and not a substitute: the aggregate
 * forwards this operator's own token to all three services, so what comes back is what they were
 * entitled to read and each layer reports its own verdict in `sources` whether it answered or not.
 *
 * **A layer can now be lost without the board being lost.** It could not before: three fetches in a
 * `Promise.all` meant one refusal became one state for the whole page, which this file argued for on
 * the grounds that a queue showing two of three sources *with no explanation* is worse than one
 * showing none. The explanation is now on the wire, so the board draws what came back and names what
 * did not.
 *
 * Because every row is already here, the toolbar works: search, ordering, paging and export are over
 * the queue the one request brought back. That is not a substitute for server-side search on the
 * listings that need one — see R-01 in the reconciliation — it is what a bounded triage set makes
 * possible for nothing.
 *
 * **The layout is the design's two-column body grid**, `minmax(0, 1fr) 420px` at 1440, where the right
 * column is the detail panel for the selected row. Below 1240 — the design's own breakpoint — the same
 * panel becomes an overlay over the table, opened by clicking a row and closed by the button in its
 * header. Both are decided in CSS from the viewport, because that is what the design measures too.
 *
 * Every measurement is read from the rendered design rather than from its source: the body grid is
 * `minmax(0, 1fr) 420px`, the page body is padded `20px 22px 32px` with a 16px gap, the header grid is
 * `minmax(170px, .9fr) minmax(260px, 1.7fr) minmax(190px, 1.1fr) 110px 80px 24px` with a 14px gap, the
 * tiles are 130 tall in a four-track grid with a 10px gap and a 14px radius, and the tab bar is a
 * 10px-radius strip of `--muted` with 3px of padding and 2px between tabs.
 */

/** The design's own rows-per-page choices, in its order. */
const PER_PAGE_CHOICES = [5, 10, 25, 50] as const;

/** The design's sort options, in its order and its words. */
const ORDER_CHOICES: readonly { readonly value: TriageOrder; readonly label: string }[] = [
  { value: "default", label: "Default order" },
  { value: "state", label: "State" },
  { value: "az", label: "Name A–Z" },
  { value: "za", label: "Name Z–A" },
];

/**
 * The scopes any action on the detail panel would need.
 *
 * An operator holding none of them is view-only, which the design draws as a chip beside the title and
 * every action visible but disabled. Holding one of them is enough to stop being a viewer: the panel
 * disables what a specific action needs, and this is about the page as a whole.
 *
 * Unchanged by the aggregate, and that is the point of it: every command on this panel goes to the
 * service that owns the exception. EXP-008 serves reads only, so `dispatch-console:read` says nothing
 * about whether this operator may act.
 */
const ACTING_SCOPES = [
  "delivery-exception:write",
  "logistics-exception:update",
  "ride-assignment:commit",
  "dispatch:orchestrate",
] as const;

/** The one scope the page itself asks for, which grants the right to ask and nothing else. */
const GATE_SCOPE = ["dispatch-console:read"] as const;

/**
 * The three layers the aggregate reports on, by the names it gives them, against the three the
 * design names in its tabs.
 *
 * Two vocabularies rather than one, and the mapping is written out rather than derived: the layer is
 * a `SourceReport.layer` value published by EXP-008, and the source is the word the design puts on a
 * tab and in the bounded note. A report whose layer is not one of these three is ignored rather than
 * guessed at.
 */
const LAYERS_REPORTED: Readonly<Record<string, TriageSource>> = {
  deliveryExceptions: "delivery",
  logisticsExceptions: "logistics",
  dispatchOperations: "dispatch",
};

export interface ExceptionsBoardProps {
  readonly transport: Transport;
  readonly tenantId: string;
  /**
   * Breadcrumbs, innermost last.
   *
   * At the top of the page body, which is where both console design files put them — neither renders
   * the shell header's own `crumbBar`.
   */
  readonly crumbs?: readonly string[];
  /** What this operator holds. Decides the view-only chip and whether the panel's actions are live. */
  readonly scopes?: ReadonlySet<string>;
  /** Injected so the clocks and the breaching rule can be exercised without waiting. */
  readonly now?: () => number;
}

/** What one request to the aggregate left the board holding. */
interface Loaded {
  /** The queue, in the design's words, in the aggregate's order. */
  readonly rows: readonly TriageRow[];
  /** Whether the counts beside the tabs may be read as totals. */
  readonly countsAreTotals: boolean;
  /** Which sources bounded or withheld their count, and why. */
  readonly countFloors: readonly CountFloor[];
  /** One verdict per layer that did not answer, as a state the board can say out loud. */
  readonly lost: readonly { readonly source: string; readonly state: ViewState }[];
}

/** A row's identity across the three sources, which only need to be unique within themselves. */
function keyOf(row: TriageRow): string {
  return `${row.source}:${row.id}`;
}

export function ExceptionsBoard({
  transport,
  tenantId,
  crumbs,
  scopes,
  now,
}: ExceptionsBoardProps): ReactNode {
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [source, setSource] = useState<TriageSource | "all">("all");
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState("");
  const [order, setOrder] = useState<TriageOrder>("default");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [selectedKey, setSelectedKey] = useState<string | undefined>(undefined);
  const [detailOpen, setDetailOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const exportRegion = useRef<HTMLSpanElement>(null);

  const load = useCallback(async () => {
    try {
      /*
       * One read.
       *
       * No `Promise.all` and no three clients: the three layers are settled inside EXP-008, which
       * never lets one of them fail the aggregate. What this `catch` handles is the case the fan-out
       * cannot — the aggregate itself refusing or being unreachable, which is one failure rather
       * than three.
       */
      const view =
        await createDispatchConsoleBffClient(transport).getDispatchConsoleExceptions(tenantId);
      setLoaded({
        rows: view.rows.map(fromAggregate),
        countsAreTotals: view.countsAreTotals,
        countFloors: view.countFloors,
        lost: lostLayers(view.sources),
      });
      setFailure(undefined);
    } catch (error) {
      /*
       * One state for the whole board, because there is no board.
       *
       * The aggregate answers 403 rather than 200 when all three layers were refused — an operator
       * holding none of the three read scopes is not an outage — and puts the three reports in
       * `details.sources`. The scopes it names are the ones to ask for; `dispatch-console:read` is
       * the gate and this operator has already passed it, so naming that would send them to ask for
       * the one permission they hold. It is only the right answer when no reports came with the
       * refusal, which is the `insufficient_scope` case.
       */
      const refused = reportsIn(error);
      const wanted = refused === undefined ? [] : missingScopesIn(refused);
      setFailure(stateForError(error, wanted.length > 0 ? wanted : GATE_SCOPE));
      setLoaded(undefined);
    }
  }, [transport, tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  // The export menu closes on Escape and on a click outside, which the design file never needed to
  // handle because it is a reference rather than something an operator uses for a whole shift.
  useEffect(() => {
    if (!exportOpen) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setExportOpen(false);
    };
    const onPointer = (event: MouseEvent): void => {
      const region = exportRegion.current;
      if (region !== null && !region.contains(event.target as Node)) setExportOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return (): void => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [exportOpen]);

  const at = now?.() ?? Date.now();
  const view = useMemo(
    () =>
      triagePage(loaded?.rows ?? [], {
        source,
        ...(status === undefined ? {} : { status }),
        search,
        order,
        page,
        perPage,
      }),
    [loaded, source, status, search, order, page, perPage],
  );

  const state: ViewState =
    failure ??
    (loaded === undefined
      ? { kind: "loading" }
      : loaded.rows.length === 0
        ? { kind: "empty", because: "no_data" }
        : { kind: "ready", stale: false });

  const statuses = useMemo(
    () => [...new Set((loaded?.rows ?? []).map(badgeLabel))].sort(),
    [loaded],
  );

  // The design opens with a row already selected, so the panel is never an empty column: the first row
  // of the queue is the one an operator was going to look at anyway.
  const selected = view.rows.find((row) => keyOf(row) === selectedKey) ?? view.rows[0] ?? undefined;
  const viewOnly = scopes !== undefined && !ACTING_SCOPES.some((scope) => scopes.has(scope));

  const clearFilters = (): void => {
    setSearch("");
    setStatus(undefined);
    setPage(1);
  };

  /*
   * The heading row, hoisted because the loading state draws it too.
   *
   * In `02 Console Shell v3` the headings sit outside the state switch: the design shimmers the
   * rows beneath a heading row that never moves, so the board does not jump when the data lands.
   * One element rather than two copies of this grid, which would be free to disagree. Q-287.
   */
  const head = (
    <div className="board__head">
      <span>State</span>
      <span>What happened</span>
      <span>Job · where</span>
      <span className="board__head--right">Clock</span>
      <span className="board__head--right board__head--open">Open</span>
      <span />
    </div>
  );

  return (
    <div className="board-split">
      <section className="page board">
        <PageHeading
          crumbs={crumbs ?? []}
          title="Exceptions"
          {...(viewOnly
            ? {
                aside: (
                  <span className="board__role-note">
                    <span aria-hidden="true" className="board__role-glyph">
                      visibility
                    </span>
                    View only · delivery-exception:read
                  </span>
                ),
              }
            : {})}
        />

        <div className="board__tiles">
          {TRIAGE_TILES.map((tile) => (
            <div
              key={tile.bucket}
              className={
                // The first tile is ringed whenever it is not empty, so it is obvious from across a
                // room. Ringing it when it is empty would train an operator to ignore the ring.
                tile.bucket === "breaching" && view.buckets.breaching > 0
                  ? "board__tile board__tile--urgent"
                  : "board__tile"
              }
            >
              <div className="board__tile-head">
                <span className="board__tile-label">{tile.label}</span>
                <span
                  aria-hidden="true"
                  className={`board__tile-glyph board__tile-glyph--${tile.bucket}`}
                >
                  {tile.glyph}
                </span>
              </div>
              <span className="board__tile-count">{view.buckets[tile.bucket]}</span>
              <span className="board__tile-detail">{tile.detail}</span>
            </div>
          ))}
        </div>

        <div className="board__panel">
          <div className="board__tabs-row">
            <div className="board__tabs" role="group" aria-label="Source">
              {TRIAGE_SOURCES.map((tab) => (
                <button
                  key={tab.source}
                  type="button"
                  aria-pressed={source === tab.source}
                  className={source === tab.source ? "board__tab board__tab--on" : "board__tab"}
                  onClick={() => {
                    setSource(tab.source);
                    setPage(1);
                  }}
                >
                  <span aria-hidden="true" className="board__tab-glyph">
                    {tab.glyph}
                  </span>
                  {tab.label}
                  <span className="board__tab-count">{view.counts[tab.source]}</span>
                </button>
              ))}
            </div>
            <span className="board__sorted">Sorted by urgency · {view.total} shown</span>
          </div>

          <div className="board__toolbar">
            <span className="board__search">
              <span aria-hidden="true" className="board__search-glyph">
                search
              </span>
              <input
                id="exceptions-search"
                type="search"
                aria-label="Search"
                placeholder="Search"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
              />
            </span>
            <select
              id="exceptions-status"
              aria-label="Filter by state"
              value={status ?? ""}
              onChange={(event) => {
                setStatus(event.target.value === "" ? undefined : event.target.value);
                setPage(1);
              }}
            >
              <option value="">All states</option>
              {statuses.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
            <select
              id="exceptions-order"
              aria-label="Sort"
              value={order}
              onChange={(event) => {
                setOrder(event.target.value as TriageOrder);
              }}
            >
              {ORDER_CHOICES.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
            <span className="board__export" ref={exportRegion}>
              <button
                type="button"
                className="board__export-button"
                aria-expanded={exportOpen}
                onClick={() => {
                  setExportOpen((open) => !open);
                }}
              >
                <span aria-hidden="true" className="board__export-glyph">
                  download
                </span>
                Export
              </button>
              {exportOpen ? (
                <div className="board__export-menu" role="menu">
                  {EXPORTS.map((choice) => (
                    <button
                      key={choice.format}
                      type="button"
                      role="menuitem"
                      className="board__export-item"
                      onClick={() => {
                        setExportOpen(false);
                        exportRows(view.all, choice.format, at);
                      }}
                    >
                      <span aria-hidden="true" className="board__export-item-glyph">
                        {choice.glyph}
                      </span>
                      {choice.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </span>
          </div>

          <StateView
            state={state}
            purpose="Exceptions from delivery, logistics and dispatch appear here, worst first."
            onRetry={() => {
              void load();
            }}
            skeleton={{
              head,
              columns: 6,
              rowClassName: "board__row",
              // Both strings come from the trail the shell already draws, so the
              // sentence can never disagree with the breadcrumb above it.
              page: crumbs?.at(-1) ?? "this page",
              tenant: crumbs?.[0] ?? "this tenant",
            }}
          >
            {head}
            <div className="board__rows">
              {view.rows.map((row) => {
                const key = keyOf(row);
                const current = selected !== undefined && keyOf(selected) === key;
                return (
                  <button
                    key={key}
                    type="button"
                    className={`board__row board__row--${stateClassOf(row)}${current ? " board__row--current" : ""}`}
                    {...(current ? { "aria-current": true } : {})}
                    onClick={() => {
                      setSelectedKey(key);
                      setDetailOpen(true);
                    }}
                  >
                    <span className="board__cell board__cell--state">
                      <StateBadge
                        code={row.code}
                        status={row.status}
                        classKey={stateClassOf(row)}
                        label={badgeLabel(row)}
                      />
                      <span className="board__row-source">
                        <span aria-hidden="true" className="board__row-source-glyph">
                          {sourceGlyph(row.source)}
                        </span>
                        {sourceLabel(row.source)}
                      </span>
                    </span>
                    <span className="board__cell">
                      <span className="board__what">{row.what}</span>
                      <span className="board__detail">{row.detail}</span>
                    </span>
                    <span className="board__cell">
                      {/* The aggregate's own identifier, which is what the design's third column
                          shows: an operator quoting a row quotes this. */}
                      <span className="board__job">{row.id}</span>
                      {row.where === undefined ? null : (
                        <span className="board__where">{row.where}</span>
                      )}
                    </span>
                    <span className="board__cell board__cell--clock">
                      <span
                        className={
                          clockIsHot(row) ? "board__clock board__clock--hot" : "board__clock"
                        }
                      >
                        {clockText(row, at)}
                      </span>
                      <span className="board__clock-sub">{row.clockLabel}</span>
                    </span>
                    <span className="board__cell board__cell--open">
                      <span className="board__age">
                        {formatAge(at - Date.parse(row.occurredAt))}
                      </span>
                      <span className="board__clock-sub">open</span>
                    </span>
                    <span aria-hidden="true" className="board__chevron">
                      chevron_right
                    </span>
                  </button>
                );
              })}
            </div>
            {view.total === 0 ? (
              <div className="board__no-match">
                <span aria-hidden="true" className="board__no-match-glyph">
                  search_off
                </span>
                Nothing matches this search or filter.
                <button type="button" className="board__clear" onClick={clearFilters}>
                  Clear filters
                </button>
              </div>
            ) : null}
            <div className="board__pager">
              <span className="board__showing">
                {view.total === 0
                  ? "No matching rows"
                  : `Showing ${(page - 1) * perPage + 1}–${Math.min(page * perPage, view.total)} of ${view.total}`}
              </span>
              <span className="board__pager-controls">
                <select
                  id="exceptions-per-page"
                  aria-label="Rows per page"
                  value={perPage}
                  onChange={(event) => {
                    setPerPage(Number(event.target.value));
                    setPage(1);
                  }}
                >
                  {PER_PAGE_CHOICES.map((choice) => (
                    <option key={choice} value={choice}>
                      {choice} per page
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  aria-label="Previous page"
                  disabled={page <= 1}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                >
                  <span aria-hidden="true">chevron_left</span>
                </button>
                <span className="board__page">
                  Page {page} of {view.pageCount}
                </span>
                <button
                  type="button"
                  aria-label="Next page"
                  disabled={page >= view.pageCount}
                  onClick={() => setPage((current) => Math.min(view.pageCount, current + 1))}
                >
                  <span aria-hidden="true">chevron_right</span>
                </button>
              </span>
            </div>
          </StateView>

          {loaded !== undefined && !loaded.countsAreTotals && loaded.countFloors.length > 0 ? (
            /*
             * Said rather than hidden, and now said with the reason.
             *
             * The merge is bounded, and an operator working a partial queue while believing it whole
             * is the failure this line exists to prevent. What is new is which kind of partial: the
             * aggregate separates a source that had more rows than it asked for from one that did not
             * answer at all, and those are different things to do about. This board inferred the
             * first from a cursor and could not see the second, because a source that failed took the
             * whole page down.
             */
            <p className="board__bounded" role="status">
              {loaded.countFloors.map((floor) => floorSentence(floor)).join(" ")} So the counts are
              a floor rather than a total.
            </p>
          ) : null}

          {loaded !== undefined && loaded.lost.length > 0 ? (
            /*
             * Which of the three sources is missing from this queue, and what it needs.
             *
             * The board could not say this before. Three fetches in a `Promise.all` meant a refusal
             * on one became one state for the whole page — defensible, because a queue showing two of
             * three sources with no explanation is worse than one showing none, and the explanation
             * did not exist. It does now: every layer reports its own verdict whether it answered or
             * not, so the rows that came back are worth working and the gap in them has a name.
             */
            <div className="board__lost" role="status">
              <span aria-hidden="true" className="board__lost-glyph">
                layers_clear
              </span>
              <span>
                Not in this queue:{" "}
                {loaded.lost.map((entry) => sourceLabel(entry.source)).join(", ")}.{" "}
                {loaded.lost.map((entry) => lostSentence(entry.state)).join(" ")}
              </span>
            </div>
          ) : null}
        </div>
      </section>

      {selected === undefined ? null : (
        <ExceptionDetail
          row={selected}
          now={at}
          viewOnly={viewOnly}
          open={detailOpen}
          onClose={() => {
            setDetailOpen(false);
          }}
        />
      )}
    </div>
  );
}

/** The export formats the design's menu offers, in its order and its words. */
const EXPORTS = [
  { format: "csv", glyph: "description", label: "CSV" },
  { format: "xls", glyph: "table_view", label: "Excel (.xls)" },
  { format: "pdf", glyph: "picture_as_pdf", label: "PDF" },
] as const;

type ExportFormat = (typeof EXPORTS)[number]["format"];

/**
 * Export what the operator is looking at, in the columns they are looking at.
 *
 * The filtered and ordered set rather than the page: an operator exporting a board wants the queue,
 * not the ten rows that happened to be on screen. Excel takes an HTML table, which is what
 * `application/vnd.ms-excel` has always meant in practice, and PDF is the browser's own print dialogue
 * — the alternative is a PDF library in a console bundle to lay out six columns.
 */
function exportRows(rows: readonly TriageRow[], format: ExportFormat, now: number): void {
  const header = ["State", "What happened", "Job", "Clock", "Open", "Source"];
  const body = rows.map((row) => [
    badgeLabel(row),
    `${row.what} — ${row.detail}`,
    row.job,
    `${clockText(row, now)} ${row.clockLabel}`.trim(),
    formatAge(now - Date.parse(row.occurredAt)),
    `${sourceLabel(row.source)} · ${row.code}`,
  ]);

  if (format === "csv") {
    const quote = (value: string): string => `"${value.replaceAll('"', '""')}"`;
    const csv = [header, ...body].map((line) => line.map(quote).join(",")).join("\n");
    download(new Blob([csv], { type: "text/csv" }), "exceptions.csv");
    return;
  }

  const table =
    '<table border="1" cellpadding="6" style="border-collapse:collapse;font-family:sans-serif;font-size:12px">' +
    `<tr>${header.map((cell) => `<th>${escapeHtml(cell)}</th>`).join("")}</tr>` +
    body
      .map((line) => `<tr>${line.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`)
      .join("") +
    "</table>";

  if (format === "xls") {
    download(
      new Blob([`<html><head><meta charset="utf-8"></head><body>${table}</body></html>`], {
        type: "application/vnd.ms-excel",
      }),
      "exceptions.xls",
    );
    return;
  }

  const printable = window.open("", "_blank");
  if (printable === null) return;
  printable.document.write(
    `<html><head><title>exceptions</title></head><body><h2 style="font-family:sans-serif">exceptions</h2>${table}</body></html>`,
  );
  printable.document.close();
  printable.focus();
  printable.print();
}

function escapeHtml(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

function download(blob: Blob, name: string): void {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = name;
  document.body.append(link);
  link.click();
  setTimeout(() => {
    URL.revokeObjectURL(link.href);
    link.remove();
  }, 500);
}

function sourceLabel(source: string): string {
  return source === "delivery" ? "Delivery" : source === "logistics" ? "Logistics" : "Dispatch";
}

/**
 * Why one source's count is a floor, in a sentence.
 *
 * Two reasons and they are different things for an operator to do about: a source with more rows
 * than the aggregate asked for has a queue longer than this board, and one that did not answer has a
 * queue this board cannot see at all. R-04 is why neither can simply be a number.
 */
function floorSentence(floor: CountFloor): string {
  return floor.reason === "truncated"
    ? `${sourceLabel(floor.source)} has more than this board asked ${floor.service} for.`
    : `${sourceLabel(floor.source)} did not answer, so nothing from ${floor.service} is counted.`;
}

/** What to do about a layer that is missing, from the state that says why it is missing. */
function lostSentence(state: ViewState): string {
  if (state.kind === "forbidden") {
    return state.missingScopes.length === 0
      ? "Your role does not include it."
      : `It needs ${state.missingScopes.join(" and ")}.`;
  }
  return state.kind === "error" ? `${state.message}.` : "";
}

/** The design's own glyph per source, on the row and on the tab. */
function sourceGlyph(source: TriageSource): string {
  return source === "delivery"
    ? "local_shipping"
    : source === "logistics"
      ? "inventory_2"
      : "alt_route";
}

/**
 * What the clock column says, which is a different thing per source.
 *
 * A dispatch search counts down to its deadline, or shows the attempts it used up once it has failed.
 * A delivery exception counts down to its retry, where one is scheduled. A logistics exception has no
 * clock at all — what it has is a severity, which is what the column shows instead. Each is that
 * source's own published field, which is why none of them is a normalised "urgency".
 */
function clockText(row: TriageRow, now: number): string {
  if (row.source === "dispatch" && row.status === "failed") return String(row.attempt ?? 0);
  if (row.source === "logistics") return row.severity ?? "—";
  if (row.deadlineAt === undefined) return "—";
  const remaining = Date.parse(row.deadlineAt) - now;
  if (Number.isNaN(remaining)) return "—";
  return remaining > 0 ? formatClock(remaining) : "Due";
}

/** A hot clock is drawn larger and in the danger foreground: it is the row to look at. */
function clockIsHot(row: TriageRow): boolean {
  if (row.source === "dispatch" && row.status === "failed") return true;
  if (row.source === "logistics") return row.severity === "critical";
  // The aggregate decided this against its own `breachingWithinMs`, at the instant it assembled the
  // queue, for the tile above the table as well. The screen comparing a clock to a threshold of its
  // own was the second implementation of one rule.
  return row.source === "dispatch" && row.bucket === "breaching";
}

/**
 * One layer's verdict as a state the board can say out loud, for the layers that did not answer.
 *
 * The same mapping `stateForError` makes from an `ApiError`, read off the report instead of off a
 * throw — because that is what the aggregate turned the throw into. A layer that answered produces
 * nothing, so the strip at the foot is empty when the whole queue is there.
 */
function lostLayers(
  reports: readonly SourceReport[],
): { readonly source: string; readonly state: ViewState }[] {
  const lost: { source: string; state: ViewState }[] = [];
  for (const report of reports) {
    const source = LAYERS_REPORTED[report.layer];
    if (source === undefined) continue;
    if (report.status === "ok") continue;
    lost.push({
      source,
      state:
        report.status === "forbidden" || report.status === "unauthorized"
          ? { kind: "forbidden", missingScopes: report.scopes }
          : {
              kind: "error",
              code: report.code ?? `${report.layer}_${report.status}`,
              message: `${report.service} did not answer`,
              retryable: report.status !== "invalid",
              offline: false,
            },
    });
  }
  return lost;
}

/** Every scope the refused layers named, deduplicated, so a refusal can be acted on by name. */
function missingScopesIn(reports: readonly SourceReport[]): string[] {
  const scopes = new Set<string>();
  for (const report of reports) {
    if (report.status !== "forbidden" && report.status !== "unauthorized") continue;
    for (const scope of report.scopes) scopes.add(scope);
  }
  return [...scopes].sort();
}

/**
 * The layer verdicts a refusal carried, if it carried any.
 *
 * Read defensively rather than cast: these come off an error body, so a report missing its layer,
 * its status or its scopes is dropped rather than turned into a sentence about an unnamed permission.
 */
function reportsIn(error: unknown): readonly SourceReport[] | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const sources = error.details?.["sources"];
  if (!Array.isArray(sources)) return undefined;
  const reports: SourceReport[] = [];
  for (const entry of sources as readonly unknown[]) {
    if (typeof entry !== "object" || entry === null) continue;
    const record = entry as Record<string, unknown>;
    const { layer, service, status, scopes } = record;
    if (typeof layer !== "string" || typeof service !== "string") continue;
    if (typeof status !== "string" || !Array.isArray(scopes)) continue;
    reports.push({
      layer,
      service,
      status: status as SourceReport["status"],
      scopes: scopes.filter((scope): scope is string => typeof scope === "string"),
      ...(typeof record["code"] === "string" ? { code: record["code"] } : {}),
    });
  }
  return reports.length === 0 ? undefined : reports;
}

/**
 * One aggregate row in the design's words.
 *
 * This is the whole of what the move left behind, and it is worth being precise about the division.
 * The aggregate decided which rows are on the board, what order they are in, which tile each is
 * counted under and which published fields travel with it. What it did not decide — because a
 * response cannot carry a sentence without a service choosing what an operator reads — is the
 * headline, the line beneath it, the caption under the clock and the field table's order. Those are
 * the design's, and they are built here from each source's own published detail.
 *
 * A row whose detail did not arrive is drawn from what every row carries. That cannot happen against
 * the published contract — `source` and the matching detail object travel together — and the
 * alternative to handling it is a crash on a response this screen cannot vouch for.
 */
function fromAggregate(row: AggregateRow): TriageRow {
  if (row.source === "delivery" && row.delivery !== undefined) {
    return fromDelivery(row, row.delivery);
  }
  if (row.source === "logistics" && row.logistics !== undefined) {
    return fromLogistics(row, row.logistics);
  }
  if (row.source === "dispatch" && row.dispatch !== undefined) {
    return fromDispatch(row, row.dispatch);
  }
  return {
    source: row.source,
    bucket: row.bucket,
    code: row.service,
    id: row.id,
    status: row.status,
    ...(row.severity === undefined ? {} : { severity: row.severity }),
    what: row.status,
    detail: `${row.service} answered no detail for this row`,
    job: row.job,
    ...(row.deadlineAt === undefined ? {} : { deadlineAt: row.deadlineAt }),
    clockLabel: "",
    version: row.version ?? 0,
    occurredAt: row.occurredAt,
    occurredAtField: row.occurredAtField,
    serviceName: row.service,
    fields: [{ name: "id", value: row.id, mono: true }],
  };
}

function fromDelivery(row: AggregateRow, detail: DeliveryDetail): TriageRow {
  const fields: DetailField[] = [
    { name: "exceptionId", value: row.id, mono: true },
    { name: "kind", value: detail.kind },
    { name: "reasonCode", value: detail.reasonCode },
    { name: "status", value: row.status },
    {
      name: "deliveryOrder",
      value: `${detail.orderId} · v${detail.orderVersion}`,
      mono: true,
    },
    detail.retryAt === null
      ? { name: "retryAt", value: "—" }
      : { name: "retryAt", value: detail.retryAt, instant: true },
    { name: "version", value: String(row.version ?? 0) },
  ];

  return {
    source: "delivery",
    bucket: row.bucket,
    code: row.service,
    id: row.id,
    status: row.status,
    what: `${readableReason(detail.reasonCode)}${detail.kind === "pickup_failed" ? " (pickup)" : ""}`,
    detail:
      row.status === "retry_scheduled"
        ? `Retry scheduled · ${countOf(detail.evidenceReferences.length, "evidence item")}`
        : row.status === "return_to_origin"
          ? "Returning to sender"
          : `${detail.kind} · reasonCode ${detail.reasonCode} · ${detail.evidenceReferences.length} evidence`,
    job: detail.orderId,
    ...(row.deadlineAt === undefined ? {} : { deadlineAt: row.deadlineAt }),
    clockLabel: row.clock === "retry" ? "to retry" : "",
    version: row.version ?? 0,
    occurredAt: row.occurredAt,
    occurredAtField: row.occurredAtField,
    serviceName: `${row.service} delivery exception`,
    fields,
    evidence: detail.evidenceReferences,
  };
}

function fromLogistics(row: AggregateRow, detail: LogisticsDetail): TriageRow {
  return {
    source: "logistics",
    bucket: row.bucket,
    code: row.service,
    id: row.id,
    status: row.status,
    ...(row.severity === undefined ? {} : { severity: row.severity }),
    // The design takes the first sentence: a summary is one line on a row and the whole of it in the
    // panel, and LOG-11 constrains neither length nor punctuation.
    what: detail.summary.split(";")[0]!.split(".")[0]!,
    detail: `${detail.subjectType} · ${detail.code}`,
    job: detail.subjectId,
    clockLabel: "severity",
    version: row.version ?? 0,
    occurredAt: row.occurredAt,
    occurredAtField: row.occurredAtField,
    serviceName: `${row.service} logistics exception`,
    fields: [
      { name: "id", value: row.id, mono: true },
      { name: "subjectType", value: detail.subjectType },
      { name: "code", value: detail.code },
      { name: "severity", value: row.severity ?? "—" },
      { name: "status", value: row.status },
      { name: "summary", value: detail.summary },
      { name: "subjectId", value: detail.subjectId, mono: true },
      { name: "version", value: String(row.version ?? 0) },
    ],
  };
}

function fromDispatch(row: AggregateRow, detail: DispatchDetail): TriageRow {
  return {
    source: "dispatch",
    bucket: row.bucket,
    // DSP-04's, not DSP-05's: the lifecycle on the badge is the saga's, and the aggregate names the
    // service it read — the projection. `StateBadge` is given the owner of the lifecycle, which is
    // the distinction its own `code` prop exists for.
    code: "DSP-04",
    id: row.id,
    status: row.status,
    what:
      row.status === "failed"
        ? "Search exhausted: no driver found"
        : "Search about to hit its deadline",
    // The design's sentences, less the "of 3": DSP-05 publishes the attempt and not the maximum.
    detail:
      row.status === "failed"
        ? `${detail.attempt} attempts used${detail.offersCancelled ? ". Offers cancelled" : ""}. The rider is still waiting on the finding screen.`
        : `Attempt ${detail.attempt}, still ${row.status.replaceAll("_", " ")}.`,
    job: row.job,
    ...(row.deadlineAt === undefined ? {} : { deadlineAt: row.deadlineAt }),
    clockLabel: row.clock === "attempts" ? "attempts used" : "to deadline",
    // The read model publishes no version: nothing on this board commands it directly.
    version: row.version ?? 0,
    occurredAt: row.occurredAt,
    occurredAtField: row.occurredAtField,
    serviceName: `${row.service} dispatch operation`,
    attempt: detail.attempt,
    offersCancelled: detail.offersCancelled,
    fields: [
      { name: "demandId", value: row.id, mono: true },
      { name: "status", value: row.status },
      // "3 of 3" in the design. DSP-05 publishes `attempt`; the maximum comes from DSP-04, which
      // nothing on this path reads, so the number here is the one the read model answers with.
      { name: "attempt", value: String(detail.attempt) },
      row.deadlineAt === undefined
        ? { name: "deadlineAt", value: "passed" }
        : { name: "deadlineAt", value: row.deadlineAt, instant: true },
      { name: "failureReason", value: detail.failureReason ?? "—" },
      { name: "offersCancelled", value: detail.offersCancelled ? "true" : "false" },
    ],
  };
}

/** "1 evidence item", "2 evidence items". */
function countOf(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

/**
 * A reason code in an operator's words.
 *
 * DEL-08's `reasonCode` is a free string constrained to `[a-z0-9_]{3,64}`, so there is no enum to map
 * and no published label. Unknown codes are shown as they are rather than guessed at, and the code
 * itself stays on the line below either way.
 */
function readableReason(code: string): string {
  const known: Record<string, string> = {
    recipient_unavailable: "Recipient unavailable",
    address_not_found: "Address not found",
    parcel_damaged: "Parcel damaged",
    merchant_closed: "Merchant closed at pickup",
    refused_by_recipient: "Refused by recipient",
    parcel_not_ready: "Parcel not ready at pickup",
    access_denied: "Gate or building access denied",
  };
  return known[code] ?? code;
}
